import { Model } from '@vuex-orm/core'
import Checkout from '@/models/user/Checkout'
import HotelSearch from '@/models/hotels/HotelSearch'
import Hotel from '@/models/hotels/Hotel'
import Customer from '@/models/user/Customer'
import Configuration from '@/models/Configuration'
import RatePlanPrice from '@/models/RatePlanPrice'
import TotalPrice from '@/models/TotalPrice'
import ExternalProduct from '@/models/ExternalProduct'
import axios, { CancelTokenSource } from 'axios'

import AppState from '@/utils/app.state'
import BookingQuote from '@/utils/BookingQuote'
import ConfigurationFee from '@/models/ConfigurationFee'
import CheckoutExtra from '@/models/CheckoutExtra'
import { GuestArrangement } from '@/composables/guests/types'

/* Service */
import {
  BookingQuoteResponse,
  cancelRequest,
  generateCancelToken,
  getBookingQuote
} from '@/data/service/bookingquote'

function hasBookingRequest() {
  const selections = Checkout.query()
    .with('ratePlanPrices')
    .with('packagePrices')
    .with('specialOfferPrices')
    .get()

  return selections &&
    selections.length &&
    selections.some(function (item) {
      return (
        item.ratePlanPrices?.isAvailableOnRequest ||
        item.packagePrices?.isAvailableOnRequest ||
        item.specialOfferPrices?.isAvailableOnRequest
      )
    })
}

/* Cancellation for the calculate total */
let calculateTotalCancellation: CancelTokenSource | null

export default class Booking extends Model {
  declare id: number
  declare hotelId: number
  declare hotelSearchId: number
  declare hotel: Hotel
  declare hotelSearch: HotelSearch
  declare checkoutItems: Checkout[]
  declare customer: Customer
  declare configurations: Configuration[]
  declare optionalExternalProducts: ExternalProduct[]
  declare paymentPrice: number
  declare totalPrice: number
  declare remain: number
  declare bookingCode?: string
  declare isLocked: boolean
  declare quoteInProgress: boolean
  declare crsBookingCode?: string
  declare bookingModificationToken?: string
  declare pendingBalance: number
  declare hasBookingRequest: boolean
  declare paidLater: number
  declare fullyPaid: boolean

  static entity = 'bookings'

  static fields() {
    return {
      id: this.uid(),
      hotelId: this.attr(null),
      hotelSearchId: this.attr(null),
      hotel: this.belongsTo(Hotel, 'hotelId'),
      hotelSearch: this.belongsTo(HotelSearch, 'hotelSearchId'),
      checkoutItems: this.hasMany(Checkout, 'bookingId'),
      customer: this.hasOne(Customer, 'bookingId'),
      configurations: this.hasMany(Configuration, 'bookingId'),
      optionalExternalProducts: this.hasMany(ExternalProduct, 'bookingId'),
      paymentPrice: this.number(0),
      totalPrice: this.number(0),
      remain: this.number(0),
      bookingCode: this.string('').nullable(),
      isLocked: this.boolean(false),
      quoteInProgress: this.boolean(false),
      crsBookingCode: this.string('').nullable(),
      bookingModificationToken: this.string('').nullable(),
      pendingBalance: this.number(0),
      hasBookingRequest: this.boolean(false),
      paidLater: this.number(0),
      fullyPaid: this.boolean(false)
    }
  }

  /**
   * Getter for instance. Creates an instance if needed
   * @returns {Booking}
   */
  static get instance() {
    const retrievedInstance = this.query().first()
    if (retrievedInstance) return retrievedInstance

    /* Create and trigger saving an instance */
    const newInstance = new this()
    newInstance.$save()

    return newInstance
  }

  static set instance(data) {
    this.update({
      where: this.instance.$id!,
      data
    })
  }

  static async calculateTotal(guestsByAccommodations: GuestArrangement[][]) {
    /* Insert dummy prices */
    Booking.insertOrUpdate({
      data: {
        id: 1,
        totalPrice: 0,
        paymentPrice: 0,
        quoteInProgress: true,
        hasBookingRequest: hasBookingRequest()
      }
    })

    /* Clear up the previous calculation */
    TotalPrice.deleteAll()

    try {
      /* Call a wrapper function */
      const response = await executeQuoteRequest(guestsByAccommodations)
      if (!response) return

      /* Save to new storage */
      BookingQuote.save(response)

      /* Iterate over */
      for (const roomConfiguration of response.roomConfigurations) {
        /* Save configuration */
        Configuration.insertOrUpdate({
          data: {
            ...roomConfiguration,
            paymentPrice: roomConfiguration.paymentPrice,
            totalPrice: roomConfiguration.totalPrice
          }
        })

        /* Check configuration fees */
        if (
          !roomConfiguration.totalPrice.length ||
          !roomConfiguration.totalPrice[0].feeBreakdown
        ) { continue }

        /* Prepare fees */
        const fees = Object
          .entries(roomConfiguration.totalPrice[0].feeBreakdown)
          .map(([id, fee]) => ({
            configurationId: roomConfiguration.identifier,
            feeId: id,
            value: fee
          }))

        /* Remove previous fees and insert new */
        ConfigurationFee
          .delete(record => +record.configurationId === +roomConfiguration.identifier)
          .then(() => ConfigurationFee.insertOrUpdate({ data: fees }))
      }

      /* 
        * Special check: partial payment fee (as requested by Reka)
        * Enforces that partial payment fee is paid on booking
        */
      const partialPaymentFeeProduct = (
        ExternalProduct.partialPaymentFeeProduct || {} as Partial<ExternalProduct>
      )
      const partialPaymentFeeId = partialPaymentFeeProduct.id

      /* Search the API response if the fee product is included */
      const partialPaymentFeeResponse = response.optionalExternalProducts.find(
        element => String(element.id) === String(partialPaymentFeeId)
      )

      /* Rewrite payment price. Subtract fee payment price and add full price */
      const paymentPrice = partialPaymentFeeResponse != null
        ? (
          response.paymentPrice -
          partialPaymentFeeResponse.paymentPrice +
          partialPaymentFeeResponse.totalPrice
        )
        : response.paymentPrice

      /* Save */
      Booking.insertOrUpdate({
        data: {
          id: 1,
          paymentPrice: paymentPrice,
          totalPrice: response.totalPrice,
          quoteInProgress: false
        }
      })
    } catch (e) {
      /* Some other error occurred */
      console.error(e)
    }
  }
}

/**
 * Make a quote request without side-effects.
 * Only object booking fee is updated to ensure correctness of request
 */
export async function executeQuoteRequest (
  guestsByAccommodations: GuestArrangement[][]
): Promise<BookingQuoteResponse | undefined> {
  /* Cancel previous requests if any */
  if (calculateTotalCancellation) {
    cancelRequest(calculateTotalCancellation)
    calculateTotalCancellation = null
  }

  /* Get the configuration */
  const configuration = Configuration.find(1)
  if (!configuration || !configuration.session) return

  /* Array of rooms */
  const selections = Checkout.query()
    .where('bookingId', 1)
    .where((item: Checkout) => !!item.roomSelectedId)
    .with(['extras'])
    .get()
    .map((item, idx) => {
      const ratePlanPrice = RatePlanPrice.find(item.ratePlanSelectedId)
      const extras = CheckoutExtra.getSelectedExtrasForCheckout(item)

      if (!ratePlanPrice) {
        throw new ReferenceError('Selected rate plan is not valid!')
      }

      const paxAges = guestsByAccommodations[idx].map(it => it.age)

      return {
        paxAges: paxAges,
        identifier: item.configurationId ? +item.configurationId : 1,
        fees: [],
        extras: Object.fromEntries(
          extras.map(extra => [extra.extraID, extra.pivot && extra.pivot.quantity])
        ),
        roomID: +item.roomSelectedId,
        ratePlanID: +ratePlanPrice.ratePlanID,
        boardCode: item.mealPlanSelectedId
      }
    })

  /* Validate data */
  if (!selections.length) return

  /* Get the count of selected physical rooms */
  const selectedPhysicalRoomsCount = Checkout.query()
    .where((item: Checkout) => item.selectedPhysicalRoomId != null)
    .count()

  /* Update booking fee ExternalProduct */
  await ExternalProduct.updateObjectBookingFeeProduct({ amount: selectedPhysicalRoomsCount })

  /* Create a request object */
  const quoteRequest = {
    propertyId: AppState.currentProperty!.toString(),
    checkin: HotelSearch.instance.startDate,
    checkout: HotelSearch.instance.endDate,
    roomConfigurations: selections,
    session: configuration.session,
    optionalExternalProducts: {}
  }

  /* Assign external products */
  const externalProducts = ExternalProduct.query().where('active', true).get()
  if (externalProducts) {
    quoteRequest.optionalExternalProducts = Object.fromEntries(
      externalProducts.map(item => [
        item.id,
        item.amount || 1
      ])
    )
  }

  /* Get cancellation token */
  calculateTotalCancellation = generateCancelToken()

  try {
    /* Make a call, allow for cancellation */
    return await getBookingQuote(
      quoteRequest,
      { cancelToken: calculateTotalCancellation }
    )
  } catch (e) {
    /* Ignore request cancellation */
    if (axios.isCancel(e)) { return }

    throw e
  }
}
